import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { Observable, Subject } from 'rxjs';
import { tap, takeUntil, debounceTime, skip } from 'rxjs/operators';

// COMPONENTS
import { RfqLineItemsFormComponent } from '../../../rfq/components/rfq-line-items-form/rfq-line-items-form.component';
import { RfqQuantityFormComponent } from '../../../rfq/components/rfq-quantity-form/rfq-quantity-form.component';
import { RfqUomFormComponent } from '../../../rfq/components/rfq-uom-form/rfq-uom-form.component';
// CONFIGS
import { RfqTableColumnConfig } from '../../../rfq/config/rfq.config';
import { DialogConfig } from '../../../shared/configs/dialog.config';
import { SnackBarConfig } from '../../../shared/configs/snackbar.config';
import { IWarehouse } from '../../../shared/interfaces/location-warehouse.interface';
import { IColumnConfig } from '../../../shared/interfaces/table.interface';
// SERVICES
import { RfqApiService } from '../../../shared/services/api/rfq/rfq-api.service';

@Component({
  selector: 'lum-order-transfer-skus',
  templateUrl: './order-transfer-skus.component.html',
  styleUrls: ['./order-transfer-skus.component.scss'],
})
export class OrderTransferSkusComponent implements OnInit, OnDestroy {
  @Input()
  form :UntypedFormGroup;

  @Input()
  itemsCost :number = 0;

  @Input()
  settings :any = {};

  @Output()
  addLineItem = new EventEmitter<{
    item :any; // TODO: Create interface
    index ?:number;
  }>();

  @Output()
  removeLineItem = new EventEmitter<number>();

  @Output()
  addRowItems = new EventEmitter<number>();

  @Output()
  selectRfq = new EventEmitter<{
    item :any; // TODO: Create interface
    tempId :number;
  }>();

  loading$ :Observable<boolean>;

  rfqs$ :Observable<any[]>; // TODO: Create type
  rfqs :any[] = [];
  searchableFields :string[] = ['sku', 'product_name'];
  searchStr :string = '';
  fromScanMode :boolean = false;
  fromEnterEvent :boolean = false; // Capture enter key event
  rfqsBySku :any = {};
  scanSearchStr :UntypedFormControl = new UntypedFormControl(null);
  relation :string = [
    'category',
    'subcategory',
    'basePrice',
    'tags.tag_details',
    'images',
    'locationStock',
    'variantParent.category',
    'variantParent.subcategory',
    'variantParent.tags.tag_details',
    'variantParent.unitOfMeasures',
    'rfqAttachments',
    'unitOfMeasures',
    'quotations',
  ].join(',');
  rfqSearchParams :{ sku :string; offset :number; per_page :number } = {
    sku: '',
    offset: 0,
    per_page: 50,
  };
  loadInitialRfq :boolean = false;
  fromPagination :boolean = false;
  rfqTableColumns :IColumnConfig[] = RfqTableColumnConfig;

  private searchUpdated$ = new Subject<string>();
  private scanSearchUpdated$ = new Subject<string>();
  private onDestroy$ = new Subject();

  constructor(
    private dialog :MatDialog,
    private cd :ChangeDetectorRef,
    private rfqApiService :RfqApiService,
    private snackBar :MatSnackBar,
  ) {}

  ngOnInit() :void {
    this.getFromService();
    this.onSearchUpdate().subscribe();
    this.onScanSearchUpdate().subscribe();
    this.getRfqs().subscribe();
    this.getRfqsLoading().subscribe();
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  onOpenLineItemsDialog() :void {
    this.dialog
      .open(RfqLineItemsFormComponent, {
        data: {
          ...this.settings,
          rfq_type: 'PRODUCT',
        },
        width: DialogConfig.sizes['xxx-large'],
        height: 'calc(100vh - 150px)',
        disableClose: true,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((lineItems) => {
        if (lineItems) {
          this.onAddLineItems(lineItems);
        }
      });
  }

  onAddLineItems(lineItems :any[], index ?:number) :void {
    lineItems.forEach((item :any) => {
      this.addLineItem.emit({ item, index });
    });
    this.cd.detectChanges();
  }

  onSearchRfqs(value :any, scanMode :boolean) :void {
    this.rfqs = [];
    this.loadInitialRfq = true;
    this.fromPagination = false;
    this.fromScanMode = scanMode;
    this.fromEnterEvent = false;
    this.rfqSearchParams = {
      ...this.rfqSearchParams,
      offset: 0,
      sku: value,
    };
    this.scanSearchUpdated$.next(value);
  }

  onPaginateRfqs(_ :any) :void {
    this.loadInitialRfq = false;
    this.fromPagination = true;
    this.rfqSearchParams.offset += this.rfqSearchParams.per_page;
    this.scanSearchUpdated$.next(this.rfqSearchParams.sku || '');
  }

  onSelectSkuFromScanMode(item :any) :void {
    this.onAddLineItems([{ ...item }]);
    this.scanSearchStr.setValue(null);
    this.rfqs = [];
  }

  onEnterRfqEvent(data :{
    item :AbstractControl; // TODO: Create interface
    index :number;
    searchStr :string;
  }) :void {
    const { item, index, searchStr } = data;
    if (searchStr) {
      this.rfqsBySku[searchStr.toLowerCase()] = { item, index };
      this.fromEnterEvent = true;
      this.fromPagination = false;
    }
  }

  onSearchInputChange(event :any) :void {
    this.searchUpdated$.next(event.target.value);
  }

  onBulkUpdateQuantity() :void {
    this.dialog
      .open(RfqQuantityFormComponent, {
        data: {},
        width: DialogConfig.sizes.small,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((formValue) => {
        if (formValue?.quantity) {
          this.lineItemsFormArray.controls
            .filter(
              (control :AbstractControl) =>
                control.get('selected')?.value && control.get('sku')?.value,
            )
            .map((control :AbstractControl) => {
              control.patchValue({
                quantity: formValue.quantity,
              });
              control.get('quantity')?.markAsDirty();
            });
        }
      });
  }

  onBulkUpdateUOM() :void {
    this.dialog
      .open(RfqUomFormComponent, {
        data: {
          lineItems: this.selectItemsValue,
        },
        width: DialogConfig.sizes.medium,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((formValue) => {
        if (formValue?.unitClassId && formValue?.uomId) {
          this.lineItemsFormArray.controls
            .filter(
              (control :AbstractControl) =>
                control.get('selected')?.value && control.get('sku')?.value,
            )
            .map((control :AbstractControl) => {
              control.patchValue({
                ordered_uom_id: formValue?.uomId,
              });
              control.get('ordered_uom_id')?.markAsDirty();
            });
        }
      });
  }

  private getFromService() :void {
    this.rfqs$ = this.rfqApiService.rfqs$;
    this.loading$ = this.rfqApiService.loading$;
  }

  private getRfqs() :Observable<any[]> {
    return this.rfqs$.pipe(
      skip(1),
      tap((rfqs :any[]) => {
        rfqs.forEach((rfq :any) => {
          this.rfqs.push(rfq);
        });

        if (this.fromPagination) {
          if (rfqs.length === 0) {
            this.snackBar.open('All SKUs successfully loaded.', 'Close', {
              duration: SnackBarConfig.DEFAULT.duration,
            });
          }
        } else if (!rfqs?.length && !this.fromEnterEvent) {
          this.snackBar.open('No SKU not found.', 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });

          this.rfqs = [];
        } else if (this.fromEnterEvent) {
          if (rfqs?.length === 1) {
            const rfq = rfqs[0];
            const rfqsBySku = this.rfqsBySku[rfq.sku?.toLowerCase()];

            if (rfqsBySku) {
              this.onAddLineItems([rfq], rfqsBySku.index);
              delete this.rfqsBySku[rfq.sku?.toLowerCase()];
            }
          } else {
            this.snackBar.open('No SKU not found.', 'Fail', {
              duration: SnackBarConfig.DEFAULT.duration,
            });
            const key = Object.keys(this.rfqsBySku)[0];
            const item = this.rfqsBySku[key]?.item;
            item.get('product_name')?.setValue('');
            this.lineItemsFormArray.setControl(this.rfqsBySku[key]?.index, item);
            this.lineItemsFormArray.updateValueAndValidity();
            delete this.rfqsBySku[key];
          }
          this.rfqs = [];
        } else if (rfqs?.length === 1 && this.fromScanMode) {
          this.onAddLineItems([rfqs[0]]);
          this.scanSearchStr.setValue(null);
          this.rfqs = [];
        }
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private onScanSearchUpdate() :Observable<string> {
    return this.scanSearchUpdated$.asObservable().pipe(
      debounceTime(500),
      tap((scanSearchStr :string) => {
        const params :{ [param :string] :string | string[] } = {
          sku: scanSearchStr,
          relations: this.relation,
          // warehouse_group_id: EWarehouseDefaultGroup.DEFAULT.toString(),
          per_page: '50',
          offset: '0',
        };
        if (this.sourceWarehouse) {
          params['warehouse_id'] = this.sourceWarehouse.id.toString();
          params['includeOnlyWarehouse'] = 'true';
        }
        this.rfqApiService.search(params);
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private onSearchUpdate() :Observable<string> {
    return this.searchUpdated$.asObservable().pipe(
      tap((searchStr :string) => {
        this.searchStr = searchStr;
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private getRfqsLoading() :Observable<boolean> {
    return this.loading$.pipe(
      tap((rfqLoading :boolean) => {
        if (!rfqLoading) {
          this.loadInitialRfq = false;
        }
      }),
      takeUntil(this.onDestroy$),
    );
  }

  get lineItemsFormArray() :UntypedFormArray {
    return this.form.get('line_items') as UntypedFormArray;
  }

  get shippingCostValue() :number {
    return this.form.get('shippingCost')?.value || 0;
  }

  get netTotal() :number {
    const itemsCost = Number(this.itemsCost);
    const shippingCostValue = Number(this.shippingCostValue);

    return itemsCost + shippingCostValue;
  }

  get selectItemsValue() :any[] {
    // TODO: Add type
    return this.lineItemsFormArray.value.filter(
      (item :any) => item.selected === true,
    );
  }

  get hasSelectedItems() :boolean {
    return this.lineItemsFormArray.value.some(
      (item :any) => item.selected === true && item.sku,
    );
  }

  get sourceWarehouse() :IWarehouse {
    return this.form.get('sourceWarehouse')?.value;
  }

  get displayColumns() :IColumnConfig[] {
    return this.rfqTableColumns;
  }
}
